import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "./api";

/**
 * Business-Central-style auto-save.
 * - Primary trigger: `flush()` fires on field-exit (blur/Tab) so edits persist the instant you leave a field.
 * - Fallback: a debounce (default 1.5s) still saves if the user never blurs (e.g. clicks a non-focusable area).
 *
 * Params:
 * - active: whether auto-save is allowed (e.g. not read-only)
 * - canSave: minimum data present to persist (e.g. customer/vendor/name chosen)
 * - createMode: record not yet persisted (new doc) — saves (creates) as soon as eligible
 * - snapshot: the payload object to watch for changes
 * - save: async fn that persists (create or update)
 *
 * Returns { status, flush } — spread `onBlur={flush}` on the card's editable container.
 */
export function useAutoSave({ active, canSave, createMode, snapshot, save, delay = 1500 }) {
  const [status, setStatus] = useState("idle"); // idle | saving | saved | error
  const timer = useRef(null);
  const last = useRef(null);
  const inflight = useRef(false);
  const snapRef = useRef(null);
  const ctx = useRef({});

  const snap = JSON.stringify(snapshot);
  snapRef.current = snap;
  ctx.current = { active, canSave, save };

  const runSave = useCallback(async () => {
    const s = snapRef.current;
    const { active, canSave, save } = ctx.current;
    if (!active || !canSave || inflight.current || s === last.current) return;
    clearTimeout(timer.current);
    inflight.current = true;
    setStatus("saving");
    try {
      await save();
      last.current = s;
      setStatus("saved");
    } catch (e) {
      setStatus("error");
      if (e?.response?.status === 400) toast.error(formatApiErrorDetail(e.response.data?.detail));
    } finally {
      inflight.current = false;
    }
  }, []);

  useEffect(() => {
    if (!active || !canSave) return;
    if (!createMode && last.current === null) { last.current = snap; return; }
    if (snap === last.current) return;
    setStatus("saving");
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { runSave(); }, delay);
    return () => clearTimeout(timer.current);
  }, [snap, active, canSave, createMode]); // eslint-disable-line react-hooks/exhaustive-deps

  return { status, flush: runSave };
}

export function AutoSaveIndicator({ status, isNew = false, testid = "autosave-indicator" }) {
  // Always-visible pill (Business Central style): dot + label.
  let key = status;
  if (status === "idle") key = isNew ? "unsaved" : "saved";
  const map = {
    saving: { text: "Saving…", dot: "bg-amber-500 animate-pulse", cls: "text-amber-700 bg-amber-50 border-amber-200" },
    saved: { text: "Saved", dot: "bg-emerald-500", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    error: { text: "Not saved", dot: "bg-rose-500", cls: "text-rose-700 bg-rose-50 border-rose-200" },
    unsaved: { text: "Not saved yet", dot: "bg-muted-foreground/50", cls: "text-muted-foreground bg-secondary border-border" },
  };
  const s = map[key] || map.saved;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${s.cls}`} data-testid={testid}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {s.text}
    </span>
  );
}
